import { ConversationMemberAvatar, Member, MemberRole } from '@/features/conversation';
import type { User } from '@/features/participant';
import { useInfiniteUserSearch } from '@/features/participant/queries/use-infinite-user-search';
import { Box, Button, Typography } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { Message } from '../../models/message';

interface UserListForwardProps {
  searchQuery: string;
  sentUsers: Map<string, Message>;
  onUserSend: (user: User) => void;
  onUserUndo: (userId: string) => void;
}

export const UserListForward: React.FC<UserListForwardProps> = ({ searchQuery, sentUsers, onUserSend, onUserUndo }) => {
  const { t } = useTranslation();
  const { allUsers, loadMoreUsers, isLoadingMore, canLoadMore, isLoading } = useInfiniteUserSearch({
    searchTerm: searchQuery,
    enabled: true,
    pageSize: 20
  });

  const handleSendClick = useCallback(
    (user: User, event: React.MouseEvent) => {
      event.stopPropagation();
      onUserSend(user);
    },
    [onUserSend]
  );

  const handleUndoClick = useCallback(
    (userId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      onUserUndo(userId);
    },
    [onUserUndo]
  );

  const getUserAvatar = (user: User) => {
    return (
      <ConversationMemberAvatar
        member={
          new Member({
            user,
            isOnline: true,
            userId: user.id,
            role: MemberRole.MEMBER
          })
        }
      />
    );
  };

  if (isLoading) {
    return (
      <Box className='mex:p-4'>
        <Typography variant='body2' color='text.secondary'>
          {t('messages.forward.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className='mex:px-4 mex:py-3'>
      <Typography variant='subtitle1' className='mex:mb-3 mex:font-semibold mex:text-gray-800'>
        {t('messages.forward.label')}
      </Typography>

      {allUsers.length === 0 ? (
        <Typography variant='body2' color='text.secondary' className='mex:py-8 mex:text-center'>
          {searchQuery ? t('messages.forward.noUsers') : t('messages.forward.emptyUsers')}
        </Typography>
      ) : (
        <Box className='mex:space-y-2'>
          {allUsers.map((user) => {
            const isSent = sentUsers.has(user.id);

            return (
              <Box
                key={user.id}
                className={`mex:flex mex:cursor-pointer mex:items-center mex:justify-between mex:rounded-lg mex:p-2 mex:transition-colors mex:hover:bg-gray-200 ${isSent ? 'mex:border mex:border-green-200 mex:bg-green-50' : ''}`}
              >
                <Box className='mex:flex mex:min-w-0 mex:flex-1 mex:items-center mex:space-x-3'>
                  {getUserAvatar(user)}
                  <Box className='mex:min-w-0 mex:flex-1'>
                    <Typography variant='body1' className='mex:truncate mex:font-medium mex:text-gray-900'>
                      {user.fullName || user.userName}
                    </Typography>
                    {user.chucVu && (
                      <Typography variant='body2' className='mex:mt-1 mex:truncate mex:text-gray-500'>
                        {user.chucVu}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {isSent ? (
                  <Button
                    variant='outlined'
                    size='small'
                    className='mex:!border-orange-300 mex:!text-orange-600 mex:!rounded-full mex:!px-3 mex:!py-1 mex:!min-w-0 mex:!text-xs mex:!font-medium mex:hover:!bg-orange-50'
                    onClick={(e) => handleUndoClick(user.id, e)}
                  >
                    {t('messages.forward.undoBtn')}
                  </Button>
                ) : (
                  <Button
                    variant='contained'
                    size='small'
                    className='mex:!bg-blue-500 mex:!text-white mex:!rounded-full mex:!px-3 mex:!py-1 mex:!min-w-0 m x:!text-xs mex:!font-medium'
                    onClick={(e) => handleSendClick(user, e)}
                  >
                    {t('messages.forward.sentBtn')}
                  </Button>
                )}
              </Box>
            );
          })}

          {canLoadMore && !isLoadingMore && (
            <Box className='mex:pt-2 mex:text-center'>
              <Button
                variant='text'
                size='small'
                onClick={loadMoreUsers}
                className='mex:!text-blue-500 mex:hover:!text-blue-700'
              >
                {t('messages.forward.viewOriginal')}
              </Button>
            </Box>
          )}

          {isLoadingMore && (
            <Box className='mex:pt-2 mex:text-center'>
              <Typography variant='body2' className='mex:text-gray-500'>
                {t('messages.forward.loading')}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
